import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { LatLngTuple, LatLngBounds } from 'leaflet'; // Updated import
import TestComponent from './TestComponent';
import { ApiRequest } from 'GlobalFunctions';
import UsersList from './components/Users';
import ClientsList from './components/Clients';  // Add this import
import LoadingCircle from 'elements/LoadingCircle';

import DateRange from 'elements/DateRange';
import moment from 'moment';
import { Divider, FormControlLabel, Switch } from '@mui/material';

import markerGreen from './marker_green.png';
import markerOrange from './marker_orange.png';
import markerRed from './marker_red.png';
import flagGreen from './flag_green.png';
import flagOrange from './flag_orange.png';
import flagRed from './flag_red.png';

// Color palette for user route lines
const ROUTE_LINE_COLORS = [
    '#1976d2', '#e91e63', '#9c27b0', '#ff9800', '#009688',
    '#795548', '#607d8b', '#3f51b5', '#00bcd4', '#8bc34a',
    '#ff5722', '#673ab7', '#03a9f4', '#cddc39', '#f44336'
];

// PNG marker icons for intermediate delivery status markers
const deliveryMarkerIcons: Record<string, L.Icon> = {
    total: L.icon({ iconUrl: markerGreen, iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32] }),
    parcial: L.icon({ iconUrl: markerOrange, iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32] }),
    devuelto: L.icon({ iconUrl: markerRed, iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32] }),
};

// PNG flag icons for start/end markers
const flagMarkerIcons: Record<string, L.Icon> = {
    total: L.icon({ iconUrl: flagGreen, iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32] }),
    parcial: L.icon({ iconUrl: flagOrange, iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32] }),
    devuelto: L.icon({ iconUrl: flagRed, iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32] }),
};

// Component to fit bounds when guide markers are loaded
function MapBoundsHandler({ positions }: { positions: LatLngTuple[] }) {
    const map = useMap();
    
    useEffect(() => {
        if (positions.length > 0) {
            const bounds = L.latLngBounds(positions);
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [positions, map]);
    
    return null;
}

export default function Map() {
    const [userRoutes, setUserRoutes] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [validPositions, setValidPositions] = useState<LatLngTuple[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]); // Add this state
    const [showRouteLines, setShowRouteLines] = useState<boolean>(false);
    const [disabledRouteUserIds, setDisabledRouteUserIds] = useState<Set<string>>(new Set());
    const [formValues, setFormValues] = useState<any>({
        date: {
            startDate: moment().format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD")
        }
    });

    const getRoutes = async () => {
        setIsLoading(true);
        await ApiRequest({
            url: '/routes-map/list',
            method: 'get',
            headers: {
                'ltkn': localStorage.getItem('ltkn')
            },
            query: {
                user_ids: selectedUserIds.length > 0 ? selectedUserIds : undefined,
                client_ids: selectedClientIds.length > 0 ? selectedClientIds : undefined,
                start_date: formValues.date.startDate,
                end_date: formValues.date.endDate
            },
            setResponse: (response: any) => {
                if (response.status === 'success') {
                    setUserRoutes(response.data);
                } else {
                    console.error('Error in routes map response:', response.message);
                }
                setIsLoading(false);
            }
        });
    };

    useEffect(() => {
        getRoutes();
    }, [selectedUserIds, selectedClientIds, formValues.date]); // Update dependency array

    const handleUserSelection = (userIds: string[]) => {
        setSelectedUserIds(userIds);
    };

    const handleClientSelection = (clientIds: string[]) => { // Add this handler
        setSelectedClientIds(clientIds);
    };

    // Default center if no orders - properly typed as LatLngTuple
    const defaultCenter: LatLngTuple = [4.7110, -74.0721];
    
    useEffect(() => {
        // Extract valid positions for map fitting
        const positions: LatLngTuple[] = [];
        userRoutes.forEach(user => {
            user.guides.forEach((guide: any) => {
                if (guide.latitud && guide.longitud) {
                    const lat = parseFloat(guide.latitud);
                    const lng = parseFloat(guide.longitud);
                    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                        positions.push([lat, lng]);
                    }
                }
            });
        });
        setValidPositions(positions);
    }, [userRoutes]);
    
    // Toggle individual user route line
    const toggleUserRoute = (userId: string) => {
        setDisabledRouteUserIds(prev => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    // Build polylines per user, sorted by client_guide_id
    const routeLines = showRouteLines ? userRoutes.map((user, userIndex) => {
        // Skip disabled users
        if (disabledRouteUserIds.has(String(user.user_id))) return null;

        const sortedGuides = [...user.guides]
            .filter((guide: any) => {
                if (!guide.latitud || !guide.longitud) return false;
                const lat = parseFloat(guide.latitud);
                const lng = parseFloat(guide.longitud);
                return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
            })
            .sort((a: any, b: any) => a.client_guide_id - b.client_guide_id);

        if (sortedGuides.length < 2) return null;

        const positions: LatLngTuple[] = sortedGuides.map((guide: any) => [
            parseFloat(guide.latitud),
            parseFloat(guide.longitud)
        ]);

        const lineColor = ROUTE_LINE_COLORS[userIndex % ROUTE_LINE_COLORS.length];

        return (
            <Polyline
                key={`route-${user.user_id}`}
                positions={positions}
                pathOptions={{
                    color: lineColor,
                    weight: 3,
                    opacity: 0.8,
                    dashArray: '8, 6'
                }}
            />
        );
    }).filter(Boolean) : [];

    // Prepare markers for all guides from all users
    const allMarkers = userRoutes.flatMap(user => 
        user.guides.map((guide: any, index: number) => {
            if (guide.latitud && guide.longitud) {
                const lat = parseFloat(guide.latitud);
                const lng = parseFloat(guide.longitud);
                
                // Validate coordinates
                if (isNaN(lat) || isNaN(lng)) {
                    console.error('Invalid coordinates', { lat: guide.latitud, lng: guide.longitud });
                    return null;
                }
                
                // Check if coordinates are in valid range
                if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                    console.error('Coordinates out of range', { lat, lng });
                    return null;
                }
                
                const position: LatLngTuple = [lat, lng];
                
                // Check if this is the last guide
                const isLastGuide = guide.last_guide === true;
                
                // Determine marker icon
                let markerIcon;
                if (isLastGuide) {
                    // Start/end markers use flag PNGs based on status_id
                    if (guide.status_id === 2) {
                        markerIcon = flagMarkerIcons.total;
                    } else if (guide.status_id === 3) {
                        markerIcon = flagMarkerIcons.parcial;
                    } else if (guide.status_id === 4) {
                        markerIcon = flagMarkerIcons.devuelto;
                    } else {
                        markerIcon = flagMarkerIcons.devuelto;
                    }
                } else {
                    // Intermediate markers use PNG based on status_id
                    if (guide.status_id === 2) {
                        markerIcon = deliveryMarkerIcons.total;
                    } else if (guide.status_id === 3) {
                        markerIcon = deliveryMarkerIcons.parcial;
                    } else if (guide.status_id === 4) {
                        markerIcon = deliveryMarkerIcons.devuelto;
                    } else {
                        markerIcon = deliveryMarkerIcons.devuelto;
                    }
                }
                
                return (
                    <Marker 
                        key={`${user.user_id}-${guide.client_guide_id || index}`} 
                        position={position}
                        icon={markerIcon}
                    >
                        <Popup>
                            <div>
                                <h3>Usuario: {user.user_name}</h3>
                                <p>Cliente: {guide.client_name} {guide.client_last_name}</p>
                                <p>Empresa: {guide.business_name}</p>
                                <p>Código Guía: {guide.guide_code}</p>
                                <p>Fecha: {(guide.updated_at || guide.created_at) ? moment(guide.updated_at || guide.created_at).format('YYYY-MM-DD hh:mm A') : 'N/A'}</p>
                                <p>Valor: ${Number(guide.guide_value || 0).toLocaleString()}</p>
                                <p>Placa: {guide.license_plate}</p>
                                <p>Código Factura: {guide.facture_code}</p>
                                <p>
                                    <div style={{ 
                                        display: 'inline-block', 
                                        backgroundColor: 
                                            guide.status_id === 2 ? '#4CAF50' : 
                                            guide.status_id === 3 ? '#FF9800' :
                                            guide.status_id === 4 ? '#F44336' : '#9E9E9E',
                                        color: 'white',
                                        padding: '3px 10px',
                                        borderRadius: '16px',
                                        fontSize: '0.85rem',
                                        fontWeight: 500
                                    }}>
                                        {guide.status_id === 2 ? 'Entrega Total' : 
                                         guide.status_id === 3 ? 'Entrega Parcial' :
                                         guide.status_id === 4 ? 'Devolución' : 'Pendiente'}
                                    </div>
                                </p>
                                {guide.priority && (
                                    <p>
                                        <div style={{ 
                                            display: 'inline-block', 
                                            backgroundColor: '#FF9800',
                                            color: 'white',
                                            padding: '3px 10px',
                                            borderRadius: '16px',
                                            fontSize: '0.75rem',
                                            fontWeight: 500
                                        }}>
                                            Prioridad: {guide.priority}
                                        </div>
                                    </p>
                                )}
                                {isLastGuide && <p><strong>Última guía</strong></p>}
                            </div>
                        </Popup>
                    </Marker>
                );
            }
            return null;
        }).filter(Boolean)
    );

    return (
        <div id="map_wrapper">
           
            <div className="map-container" style={{height: '100%'}}>
            <div className="map_top_bar">
                <DateRange
                    className="filters_datePicker"
                    label="Date range"
                    variant="outlined"
                    fullWidth
                    value={{
                        startDate: formValues.date?.startDate || null,
                        endDate: formValues.date?.endDate || null,
                    }}
                    format="MMMM DD, yyyy"
                    onChange={(e: any) => {
                        if (e.startDate && e.endDate) {
                            const startDate = moment(e.startDate).format("YYYY-MM-DD");
                            const endDate = moment(e.endDate).format("YYYY-MM-DD");

                            setFormValues({
                                ...formValues,
                                date: { startDate, endDate },
                            });
                        }
                    }}
                    variantDatePicker="side-by-side"
                />
            </div>
                {isLoading ? (
                    <LoadingCircle />
                ) : (
                    <MapContainer center={defaultCenter} zoom={13} scrollWheelZoom={false} className="map-container">
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {allMarkers}
                        {routeLines}
                        {validPositions.length > 0 && <MapBoundsHandler positions={validPositions} />}
                    </MapContainer>
                )}
            </div>

            <div className="filters-wrapper">
                <UsersList onSelectUsers={handleUserSelection} />
                <Divider />
                {/* 
                    <ClientsList onSelectClients={handleClientSelection} /> 
                    <Divider /> 
                */}

                
                <div className="filters_section" style={{ position: 'sticky', bottom: 0, background: '#fff', zIndex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h2>Recorrido</h2>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showRouteLines}
                                    onChange={(e) => setShowRouteLines(e.target.checked)}
                                    size="small"
                                />
                            }
                            label=""
                            sx={{ mr: 0 }}
                        />
                    </div>

                {showRouteLines && userRoutes.length > 0 && (
                    <div className="filters_section" style={{ padding: 0 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                                {userRoutes.map((user, idx) => {
                                    const isDisabled = disabledRouteUserIds.has(String(user.user_id));
                                    return (
                                        <div 
                                            key={user.user_id} 
                                            onClick={() => toggleUserRoute(String(user.user_id))}
                                            style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '8px',
                                                cursor: 'pointer',
                                                opacity: isDisabled ? 0.4 : 1,
                                                transition: 'opacity 0.2s',
                                                padding: '4px 6px',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            <div style={{
                                                width: '24px',
                                                height: '4px',
                                                backgroundColor: isDisabled ? '#ccc' : ROUTE_LINE_COLORS[idx % ROUTE_LINE_COLORS.length],
                                                borderRadius: '2px',
                                                flexShrink: 0
                                            }} />
                                            <span style={{ 
                                                fontSize: '0.8rem',
                                                textDecoration: isDisabled ? 'line-through' : 'none',
                                                color: isDisabled ? '#999' : 'inherit'
                                            }}>{user.user_name}</span>
                                        </div>
                                    );
                                })}
                            </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    )
}


